import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AlertTriangle, ArrowRight, Check, Copy, Loader2, Search, Trash2, X } from 'lucide-react';
import { stcBinService } from '../../../../../../services/stcBinService';
import { stcBinTypeService, StcBinTypeDto } from '../../../../../../services/stcBinTypeService';
import { stcBinSizeService } from '../../../../../../services/stcBinSizeService';
import { stcBinGrpService, StcBinGrpDto } from '../../../../../../services/stcBinGrpService';
import { stcResourceService, StcResourceDto } from '../../../../../../services/stcResourceService';
import { stcPriorityService, StcPriorityDto } from '../../../../../../services/stcPriorityService';
import { stcZoneService, StcZoneDto } from '../../../../../../services/stcZoneService';
import { stcMovementTypeService, StcMovementTypeDto } from '../../../../../../services/stcMovementTypeService';
import { StcBinDto } from '../../../../../../types/stcBin';
import { StcBinSizeDto } from '../../../../../../types/stcBinSize';
import { BulkUpdateBinsRequest, BulkOperationResponse } from '../../../rack-builder/types';
import { EnrichedBin } from './types';
import { useTranslation } from '../../../../../../contexts/I18nProvider';
import { useStatusOptions } from '../../../../../common/statusPresentation';
import { isReceiptBinPickConflict } from '../../../../../../utils/receiptBinWarning';
import { ReceiptBinWarningPanel } from './ReceiptBinWarning';

type TFunc = (key: string, params?: Record<string, string | number | null | undefined>, defaultText?: string) => string;

type Step = 'select-base' | 'select-fields' | 'preview' | 'running';
type TabType = 'basics' | 'type' | 'pickput' | 'replen';

interface StcBinCopyPropertiesModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedBinCodes: string[];
    selectedBinData: EnrichedBin[];
    companyNo: string;
    warehouseID: string;
    onSuccess: () => void;
}

interface MasterData {
    binTypes: StcBinTypeDto[];
    binSizes: StcBinSizeDto[];
    binGroups: StcBinGrpDto[];
    // binCategories fjernet 2026-07-06: kategoritekst resolves fra master-StatusCodes (ST42)
    resources: StcResourceDto[];
    priorities: StcPriorityDto[];
    zones: StcZoneDto[];
    movementTypes: StcMovementTypeDto[];
}

type FieldKey =
    | 'description1' | 'customSortID' | 'stcZoneID'
    | 'binSizeNo' | 'binTypeNo' | 'binGrpNo' | 'stcBinCatNo' | 'resourceNo' | 'movementTypeNo'
    | 'pickPriorityNo' | 'putAwayPriorityNo' | 'pickableYesNo' | 'itemNo'
    | 'replenishMinLevel' | 'replenishMaxLevel' | 'autoReplenishment'
    | 'reservationAllowed' | 'negativeReservationAllowed'
    | 'sharedBinYesNo' | 'sharedBinMaxQuantity' | 'sharedBinPreferEmptyBin';

interface FieldDef {
    key: FieldKey;
    tab: TabType;
    label: string;
    display: (bin: StcBinDto, master: MasterData) => string;
}

const EMPTY = '<tom>';

const formatBool = (v: boolean | null | undefined): string => v === true ? 'Ja' : v === false ? 'Nei' : EMPTY;
const formatStr = (v: string | null | undefined): string => (v && v.length > 0) ? v : EMPTY;
const formatNum = (v: number | null | undefined): string => (v ?? null) !== null ? String(v) : EMPTY;

const getFieldDefs = (t: TFunc, binCategoryTextMap: Map<number, string>): FieldDef[] => [
    // Tab 1: Grunnleggende
    { key: 'description1', tab: 'basics', label: t('manager.modules.adminItemsLocations.locationCopy.fieldDescription', {}, 'Beskrivelse'), display: (b) => formatStr(b.description1) },
    { key: 'stcZoneID', tab: 'basics', label: t('manager.modules.adminItemsLocations.locationCopy.fieldZone', {}, 'Sone'), display: (b, m) => {
        if (!b.stcZoneID) return EMPTY;
        const z = m.zones.find(x => x.stcZoneID === b.stcZoneID);
        return z ? `${b.stcZoneID} - ${z.description1}` : b.stcZoneID;
    }},
    { key: 'customSortID', tab: 'basics', label: t('manager.modules.adminItemsLocations.locationCopy.fieldCustomSort', {}, 'Sortering'), display: (b) => formatNum(b.customSortID) },

    // Tab 2: Type & klassifisering
    { key: 'binTypeNo', tab: 'type', label: t('manager.modules.adminItemsLocations.locationCopy.fieldBinType', {}, 'Lokasjonstype'), display: (b, m) => {
        if (b.binTypeNo == null) return EMPTY;
        const bt = m.binTypes.find(x => x.binTypeNo === b.binTypeNo);
        return bt ? `${b.binTypeNo} - ${bt.description1 ?? '?'}` : String(b.binTypeNo);
    }},
    { key: 'binSizeNo', tab: 'type', label: t('manager.modules.adminItemsLocations.locationCopy.fieldBinSize', {}, 'Størrelse'), display: (b, m) => {
        if (b.binSizeNo == null) return EMPTY;
        const s = m.binSizes.find(x => x.binSizeNo === b.binSizeNo);
        return s ? `${b.binSizeNo} - ${s.description1}` : String(b.binSizeNo);
    }},
    { key: 'binGrpNo', tab: 'type', label: t('manager.modules.adminItemsLocations.locationCopy.fieldBinGrp', {}, 'Lokasjonsgruppe'), display: (b, m) => {
        if (b.binGrpNo == null) return EMPTY;
        const g = m.binGroups.find(x => x.binGrpNo === b.binGrpNo);
        return g ? `${b.binGrpNo} - ${g.description1}` : String(b.binGrpNo);
    }},
    { key: 'stcBinCatNo', tab: 'type', label: t('manager.modules.adminItemsLocations.locationCopy.fieldCategory', {}, 'Kategori'), display: (b) => {
        if (b.stcBinCatNo == null) return EMPTY;
        // Kategoritekst fra master-StatusCodes (ST42), språkbevisst.
        const text = binCategoryTextMap.get(b.stcBinCatNo);
        return text ? `${b.stcBinCatNo} - ${text}` : String(b.stcBinCatNo);
    }},
    { key: 'resourceNo', tab: 'type', label: t('manager.modules.adminItemsLocations.locationCopy.fieldResource', {}, 'Ressurs'), display: (b, m) => {
        if (b.resourceNo == null) return EMPTY;
        const r = m.resources.find(x => x.resourceNo === b.resourceNo);
        return r ? `${b.resourceNo} - ${r.description1}` : String(b.resourceNo);
    }},
    { key: 'movementTypeNo', tab: 'type', label: t('manager.modules.adminItemsLocations.locationCopy.fieldMovementType', {}, 'Bevegelseskode'), display: (b, m) => {
        if (b.movementTypeNo == null) return EMPTY;
        const mt = m.movementTypes.find(x => x.movementTypeNo === b.movementTypeNo);
        return mt ? `${b.movementTypeNo} - ${mt.description1}` : String(b.movementTypeNo);
    }},

    // Tab 3: Plukk og innlegging
    { key: 'pickPriorityNo', tab: 'pickput', label: t('manager.modules.adminItemsLocations.locationCopy.fieldPickPriority', {}, 'Plukkprioritet'), display: (b, m) => {
        if (b.pickPriorityNo == null) return EMPTY;
        const p = m.priorities.find(x => x.priorityNo === b.pickPriorityNo);
        return p ? `${b.pickPriorityNo} - ${p.description1}` : String(b.pickPriorityNo);
    }},
    { key: 'putAwayPriorityNo', tab: 'pickput', label: t('manager.modules.adminItemsLocations.locationCopy.fieldPutAwayPriority', {}, 'Innleggingsprioritet'), display: (b, m) => {
        if (b.putAwayPriorityNo == null) return EMPTY;
        const p = m.priorities.find(x => x.priorityNo === b.putAwayPriorityNo);
        return p ? `${b.putAwayPriorityNo} - ${p.description1}` : String(b.putAwayPriorityNo);
    }},
    { key: 'pickableYesNo', tab: 'pickput', label: t('manager.modules.adminItemsLocations.locationCopy.fieldPickable', {}, 'Plukklokasjon'), display: (b) => formatBool(b.pickableYesNo) },
    { key: 'itemNo', tab: 'pickput', label: t('manager.modules.adminItemsLocations.locationCopy.fieldDedicatedItem', {}, 'Dedikert vare'), display: (b) => formatStr(b.itemNo) },

    // Tab 4: Påfylling, reservasjon og deling
    { key: 'replenishMinLevel', tab: 'replen', label: t('manager.modules.adminItemsLocations.locationCopy.fieldReplenishMin', {}, 'Påfyll min-nivå'), display: (b) => formatNum(b.replenishMinLevel) },
    { key: 'replenishMaxLevel', tab: 'replen', label: t('manager.modules.adminItemsLocations.locationCopy.fieldReplenishMax', {}, 'Påfyll maks-nivå'), display: (b) => formatNum(b.replenishMaxLevel) },
    { key: 'autoReplenishment', tab: 'replen', label: t('manager.modules.adminItemsLocations.locationCopy.fieldAutoReplenish', {}, 'Auto-påfylling'), display: (b) => formatBool(b.autoReplenishment) },
    { key: 'reservationAllowed', tab: 'replen', label: t('manager.modules.adminItemsLocations.locationCopy.fieldReservationAllowed', {}, 'Reservering tillatt'), display: (b) => formatBool(b.reservationAllowed) },
    { key: 'negativeReservationAllowed', tab: 'replen', label: t('manager.modules.adminItemsLocations.locationCopy.fieldNegativeReservation', {}, 'Negativ reservering tillatt'), display: (b) => formatBool(b.negativeReservationAllowed) },
    { key: 'sharedBinYesNo', tab: 'replen', label: t('manager.modules.adminItemsLocations.locationCopy.fieldSharedBin', {}, 'Delt lokasjon'), display: (b) => formatBool(b.sharedBinYesNo) },
    { key: 'sharedBinMaxQuantity', tab: 'replen', label: t('manager.modules.adminItemsLocations.locationCopy.fieldSharedMaxQty', {}, 'Maks antall (delt)'), display: (b) => formatNum(b.sharedBinMaxQuantity) },
    { key: 'sharedBinPreferEmptyBin', tab: 'replen', label: t('manager.modules.adminItemsLocations.locationCopy.fieldPreferEmptyShared', {}, 'Foretrekk tom (delt)'), display: (b) => formatBool(b.sharedBinPreferEmptyBin) }
];

const getTabs = (t: TFunc): { id: TabType; label: string }[] => [
    { id: 'basics', label: t('manager.modules.adminItemsLocations.locationCopy.tabBasics', {}, 'Grunnleggende') },
    { id: 'type', label: t('manager.modules.adminItemsLocations.locationCopy.tabType', {}, 'Type & klassifisering') },
    { id: 'pickput', label: t('manager.modules.adminItemsLocations.locationCopy.tabPickPut', {}, 'Plukk & innlegging') },
    { id: 'replen', label: t('manager.modules.adminItemsLocations.locationCopy.tabReplen', {}, 'Påfylling & deling') }
];

export const StcBinCopyPropertiesModal: React.FC<StcBinCopyPropertiesModalProps> = ({
    isOpen,
    onClose,
    selectedBinCodes,
    selectedBinData,
    companyNo,
    warehouseID,
    onSuccess
}) => {
    const { t } = useTranslation();
    // Lokasjonskategori (ST42): master-basert, språkbevisst (Model A).
    const binCategoryStatusOptions = useStatusOptions(42);
    const binCategoryTextMap = useMemo(
        () => new Map(binCategoryStatusOptions.map(o => [o.value, o.text])),
        [binCategoryStatusOptions]
    );
    const FIELD_DEFS = useMemo(() => getFieldDefs(t, binCategoryTextMap), [t, binCategoryTextMap]);
    const TABS = useMemo(() => getTabs(t), [t]);
    const [step, setStep] = useState<Step>('select-base');
    const [activeTab, setActiveTab] = useState<TabType>('basics');
    const [showChangesOnly, setShowChangesOnly] = useState(true);

    // Step 1: Base bin search
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<StcBinDto[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [baseBin, setBaseBin] = useState<StcBinDto | null>(null);

    // Step 2: Field selection
    const [selectedFields, setSelectedFields] = useState<Set<FieldKey>>(new Set());

    // Step 3: Targets with possible exclusions
    const [excludedBinCodes, setExcludedBinCodes] = useState<Set<string>>(new Set());

    // Step 4: Run
    const [isRunning, setIsRunning] = useState(false);
    const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);

    // Master data
    const [master, setMaster] = useState<MasterData>({
        binTypes: [], binSizes: [], binGroups: [], resources: [], priorities: [], zones: [], movementTypes: []
    });
    const [isLoadingMaster, setIsLoadingMaster] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (!isOpen) return;
        setStep('select-base');
        setActiveTab('basics');
        setSearchTerm('');
        setSearchResults([]);
        setBaseBin(null);
        setSelectedFields(new Set());
        setExcludedBinCodes(new Set());
        setIsRunning(false);
        setNotification(null);
    }, [isOpen, selectedBinCodes]);

    // Load master data
    useEffect(() => {
        if (!isOpen || !companyNo) return;
        let cancelled = false;
        setIsLoadingMaster(true);
        Promise.all([
            stcBinTypeService.getCurrent().catch(() => []),
            stcBinSizeService.getCurrent().catch(() => []),
            stcBinGrpService.getCurrent().catch(() => []),
            stcResourceService.getCurrent().catch(() => []),
            stcPriorityService.getCurrent().catch(() => []),
            stcZoneService.getCurrent().catch(() => []),
            stcMovementTypeService.getCurrent().catch(() => [])
        ]).then(([binTypes, binSizes, binGroups, resources, priorities, zones, movementTypes]) => {
            if (cancelled) return;
            setMaster({ binTypes, binSizes, binGroups, resources, priorities, zones, movementTypes });
        }).finally(() => {
            if (!cancelled) setIsLoadingMaster(false);
        });
        return () => { cancelled = true; };
    }, [isOpen, companyNo]);

    // Debounced search
    useEffect(() => {
        if (!isOpen || step !== 'select-base') return;
        if (searchTerm.trim().length < 2) {
            setSearchResults([]);
            return;
        }
        const handle = setTimeout(async () => {
            setIsSearching(true);
            try {
                const results = await stcBinService.search(companyNo, searchTerm.trim());
                setSearchResults(results.slice(0, 50));
            } catch {
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 300);
        return () => clearTimeout(handle);
    }, [searchTerm, isOpen, step, companyNo]);

    const activeTargets = useMemo(
        () => selectedBinData.filter(b =>
            !excludedBinCodes.has(b.binCode) &&
            (!baseBin || b.binCode !== baseBin.binCode)
        ),
        [selectedBinData, excludedBinCodes, baseBin]
    );

    // Mål-lokasjoner som blir mottakslokasjon + plukkbar/reserverbar etter kopieringen (TW 36401238).
    // Resultat per mål = basisverdi for valgte felt, ellers målets nåværende verdi.
    const receiptConflictBinCodes = useMemo(() => {
        if (!baseBin) return [];
        const pick = <K extends 'stcBinCatNo' | 'pickableYesNo' | 'reservationAllowed'>(target: EnrichedBin, key: K) =>
            selectedFields.has(key) ? baseBin[key] : target[key];
        return activeTargets
            .filter(target => isReceiptBinPickConflict({
                stcBinCatNo: pick(target, 'stcBinCatNo'),
                pickableYesNo: pick(target, 'pickableYesNo'),
                reservationAllowed: pick(target, 'reservationAllowed')
            }))
            .map(target => target.binCode);
    }, [activeTargets, baseBin, selectedFields]);

    const fieldDefsByTab = useMemo(() => {
        const map = new Map<TabType, FieldDef[]>();
        TABS.forEach(tab => map.set(tab.id, []));
        FIELD_DEFS.forEach(f => map.get(f.tab)!.push(f));
        return map;
    }, [TABS, FIELD_DEFS]);

    const toggleField = (key: FieldKey) => {
        setSelectedFields(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleAllInTab = (tab: TabType, select: boolean) => {
        setSelectedFields(prev => {
            const next = new Set(prev);
            FIELD_DEFS.filter(f => f.tab === tab).forEach(f => {
                if (select) next.add(f.key);
                else next.delete(f.key);
            });
            return next;
        });
    };

    const selectAll = () => setSelectedFields(new Set(FIELD_DEFS.map(f => f.key)));
    const clearAll = () => setSelectedFields(new Set());

    const handleSelectBase = useCallback(async (bin: StcBinDto) => {
        try {
            const full = await stcBinService.getByCompanyWarehouseAndBinCode(companyNo, bin.warehouseID, bin.binCode);
            setBaseBin(full);
        } catch {
            setBaseBin(bin);
        }
    }, [companyNo]);

    const buildRequest = (): BulkUpdateBinsRequest => {
        if (!baseBin) throw new Error(t('manager.modules.adminItemsLocations.locationCopy.noBaseBin', {}, 'Ingen basislokasjon valgt'));
        const req: BulkUpdateBinsRequest = {
            companyNo,
            warehouseID,
            binCodes: activeTargets.map(b => b.binCode),
            sourceBinCode: baseBin.binCode
        };
        selectedFields.forEach(key => {
            const value = (baseBin as unknown as Record<string, unknown>)[key];
            (req as unknown as Record<string, unknown>)[key] = value ?? null;
        });
        return req;
    };

    const executeRun = async () => {
        if (!baseBin || activeTargets.length === 0 || selectedFields.size === 0) return;
        setIsRunning(true);
        setStep('running');
        setNotification(null);
        try {
            const request = buildRequest();
            const response: BulkOperationResponse = await stcBinService.bulkUpdateBins(request);
            const total = activeTargets.length;
            if (response.errors.length > 0) {
                setNotification({
                    type: 'warning',
                    message: t('manager.modules.adminItemsLocations.locationCopy.copyPartial', { '0': response.successCount, '1': total, '2': response.errors.length }, `Kopierte til ${response.successCount} av ${total} lokasjoner. ${response.errors.length} feil oppstod.`)
                });
            } else {
                setNotification({
                    type: 'success',
                    message: t('manager.modules.adminItemsLocations.locationCopy.copySuccess', { '0': response.successCount, '1': baseBin.binCode }, `${response.successCount} lokasjon(er) oppdatert fra basislokasjon ${baseBin.binCode}.`)
                });
            }
            onSuccess();
        } catch (err) {
            setNotification({
                type: 'error',
                message: err instanceof Error ? err.message : t('manager.modules.adminItemsLocations.locationCopy.copyFailed', {}, 'Kopiering feilet')
            });
            setStep('preview');
        } finally {
            setIsRunning(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[92vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <Copy className="w-6 h-6 text-blue-600" />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{t('manager.modules.adminItemsLocations.locationCopy.title', {}, 'Kopier egenskaper fra basislokasjon')}</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {t('manager.modules.adminItemsLocations.locationCopy.targetsMarked', { '0': selectedBinCodes.length }, `${selectedBinCodes.length} mål-lokasjon(er) markert`)}
                                {baseBin && <> · {t('manager.modules.adminItemsLocations.locationCopy.baseBinLabel', {}, 'basislokasjon:')} <span className="font-medium">{baseBin.binCode}</span></>}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label={t('common.buttons.close', {}, 'Lukk')}>
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Step indicator */}
                <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                    <ol className="flex items-center text-sm">
                        <StepBadge n={1} label={t('manager.modules.adminItemsLocations.locationCopy.step1', {}, 'Velg basislokasjon')} active={step === 'select-base'} done={!!baseBin && step !== 'select-base'} />
                        <Chevron />
                        <StepBadge n={2} label={t('manager.modules.adminItemsLocations.locationCopy.step2', {}, 'Velg felter')} active={step === 'select-fields'} done={selectedFields.size > 0 && step !== 'select-fields' && step !== 'select-base'} />
                        <Chevron />
                        <StepBadge n={3} label={t('manager.modules.adminItemsLocations.locationCopy.step3', {}, 'Forhåndsvisning')} active={step === 'preview'} done={step === 'running'} />
                        <Chevron />
                        <StepBadge n={4} label={t('manager.modules.adminItemsLocations.locationCopy.step4', {}, 'Kjør')} active={step === 'running'} done={false} />
                    </ol>
                </div>

                {/* Notification */}
                {notification && (
                    <div className={`mx-6 mt-4 rounded-lg p-3 flex items-start space-x-2 ${
                        notification.type === 'success' ? 'bg-green-50 border border-green-200' :
                        notification.type === 'error' ? 'bg-red-50 border border-red-200' :
                        'bg-yellow-50 border border-yellow-200'
                    }`}>
                        {notification.type === 'success' && <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />}
                        {notification.type === 'error' && <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
                        {notification.type === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />}
                        <p className={`text-sm flex-1 ${
                            notification.type === 'success' ? 'text-green-800' :
                            notification.type === 'error' ? 'text-red-800' :
                            'text-yellow-800'
                        }`}>
                            {notification.message}
                        </p>
                    </div>
                )}

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {step === 'select-base' && (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600">
                                {t('manager.modules.adminItemsLocations.locationCopy.selectBaseIntro', {}, 'Søk og velg en korrekt konfigurert lokasjon som skal brukes som mal. Verdier fra denne kopieres til de markerte mål-lokasjonene.')}
                            </p>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={t('manager.modules.adminItemsLocations.locationCopy.searchPlaceholder', {}, 'Søk på lokasjonskode eller beskrivelse (min 2 tegn)')}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    autoFocus
                                />
                            </div>
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                {isSearching && (
                                    <div className="p-6 text-center text-gray-500 flex items-center justify-center space-x-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>{t('manager.modules.adminItemsLocations.locationCopy.searching', {}, 'Søker...')}</span>
                                    </div>
                                )}
                                {!isSearching && searchTerm.trim().length < 2 && (
                                    <div className="p-6 text-center text-gray-400 text-sm">{t('manager.modules.adminItemsLocations.locationCopy.typeMin2', {}, 'Skriv minst 2 tegn for å søke')}</div>
                                )}
                                {!isSearching && searchTerm.trim().length >= 2 && searchResults.length === 0 && (
                                    <div className="p-6 text-center text-gray-400 text-sm">{t('manager.modules.adminItemsLocations.locationCopy.noHits', {}, 'Ingen treff')}</div>
                                )}
                                {!isSearching && searchResults.length > 0 && (
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th className="text-left p-2 font-medium text-gray-700">{t('warehouse.general.bin', {}, 'Lokasjon')}</th>
                                                <th className="text-left p-2 font-medium text-gray-700">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                                                <th className="text-left p-2 font-medium text-gray-700">{t('warehouse.general.warehouse', {}, 'Lager')}</th>
                                                <th className="text-left p-2 font-medium text-gray-700">{t('warehouse.general.zone', {}, 'Sone')}</th>
                                                <th className="text-right p-2"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {searchResults.map(bin => {
                                                const isTarget = selectedBinCodes.includes(bin.binCode);
                                                return (
                                                    <tr key={`${bin.warehouseID}-${bin.binCode}`} className={`border-b border-gray-100 hover:bg-blue-50 ${isTarget ? 'opacity-50' : ''}`}>
                                                        <td className="p-2 font-mono text-gray-900">{bin.binCode}</td>
                                                        <td className="p-2 text-gray-700">{bin.description1 || '—'}</td>
                                                        <td className="p-2 text-gray-500">{bin.warehouseID}</td>
                                                        <td className="p-2 text-gray-500">{bin.stcZoneID || '—'}</td>
                                                        <td className="p-2 text-right">
                                                            {isTarget ? (
                                                                <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">{t('manager.modules.adminItemsLocations.locationCopy.alreadyTarget', {}, 'Allerede mål')}</span>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleSelectBase(bin)}
                                                                    className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                                                >
                                                                    {t('manager.modules.adminItemsLocations.locationCopy.selectAsBase', {}, 'Velg som basis')}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            {baseBin && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-blue-900">{t('manager.modules.adminItemsLocations.locationCopy.selectedBaseBin', {}, 'Valgt basislokasjon')}</p>
                                            <p className="text-lg font-mono text-blue-900 mt-1">{baseBin.binCode}</p>
                                            <p className="text-sm text-blue-800">{baseBin.description1 || '—'}</p>
                                            <p className="text-xs text-blue-700 mt-2">
                                                {t('manager.modules.adminItemsLocations.locationCopy.baseSummary', { '0': baseBin.warehouseID, '1': baseBin.stcZoneID || '—', '2': baseBin.binTypeNo ?? '—', '3': baseBin.binGrpNo ?? '—' }, `Lager: ${baseBin.warehouseID} · Sone: ${baseBin.stcZoneID || '—'} · Type: ${baseBin.binTypeNo ?? '—'} · Gruppe: ${baseBin.binGrpNo ?? '—'}`)}
                                            </p>
                                            {baseBin.warehouseID !== warehouseID && (
                                                <p className="text-xs text-amber-700 mt-2 flex items-center space-x-1">
                                                    <AlertTriangle className="w-3 h-3" />
                                                    <span>{t('manager.modules.adminItemsLocations.locationCopy.otherWarehouseWarning', {}, 'Basis er i et annet lager — egenskaper kopieres uansett.')}</span>
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => setBaseBin(null)}
                                            className="text-blue-700 hover:text-blue-900 text-sm"
                                        >
                                            {t('manager.modules.adminItemsLocations.locationCopy.switch', {}, 'Bytt')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 'select-fields' && baseBin && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-600">
                                    {t('manager.modules.adminItemsLocations.locationCopy.selectFieldsIntroPrefix', {}, 'Velg hvilke felter som skal kopieres fra')} <span className="font-mono font-medium">{baseBin.binCode}</span>.
                                </p>
                                <div className="flex items-center space-x-2">
                                    <button onClick={selectAll} className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-50">{t('manager.modules.adminItemsLocations.locationCopy.selectAll', {}, 'Velg alle')}</button>
                                    <button onClick={clearAll} className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-50">{t('manager.modules.adminItemsLocations.locationCopy.clearAll', {}, 'Fjern alle')}</button>
                                </div>
                            </div>

                            {/* Tab navigation */}
                            <div className="border-b border-gray-200">
                                <div className="flex space-x-6 overflow-x-auto">
                                    {TABS.map(tab => {
                                        const tabFields = fieldDefsByTab.get(tab.id) ?? [];
                                        const checkedCount = tabFields.filter(f => selectedFields.has(f.key)).length;
                                        return (
                                            <button
                                                key={tab.id}
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                                                    activeTab === tab.id
                                                        ? 'border-blue-600 text-blue-600'
                                                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                                                }`}
                                            >
                                                {tab.label}
                                                {checkedCount > 0 && (
                                                    <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs bg-blue-100 text-blue-700 rounded-full">
                                                        {checkedCount}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Field list for current tab */}
                            <div className="space-y-1">
                                <div className="flex items-center justify-end space-x-3 pb-2">
                                    <button
                                        onClick={() => toggleAllInTab(activeTab, true)}
                                        className="text-xs text-blue-600 hover:underline"
                                    >
                                        {t('manager.modules.adminItemsLocations.locationCopy.selectAllInTab', {}, 'Velg alle i fanen')}
                                    </button>
                                    <button
                                        onClick={() => toggleAllInTab(activeTab, false)}
                                        className="text-xs text-blue-600 hover:underline"
                                    >
                                        {t('manager.modules.adminItemsLocations.locationCopy.clearAllInTab', {}, 'Fjern alle i fanen')}
                                    </button>
                                </div>
                                {(fieldDefsByTab.get(activeTab) ?? []).map(field => {
                                    const checked = selectedFields.has(field.key);
                                    const displayValue = field.display(baseBin, master);
                                    return (
                                        <label
                                            key={field.key}
                                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                                checked ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleField(field.key)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <span className="text-sm font-medium text-gray-700">{field.label}</span>
                                            </div>
                                            <span className={`text-sm font-mono ml-3 truncate max-w-md text-right ${
                                                displayValue === EMPTY ? 'text-gray-400 italic' : 'text-gray-900'
                                            }`}>
                                                {displayValue}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 'preview' && baseBin && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-600">
                                    {t('manager.modules.adminItemsLocations.locationCopy.previewIntroPrefix', {}, 'Forhåndsvisning av endringer. Trykk')} <Trash2 className="inline w-3 h-3" /> {t('manager.modules.adminItemsLocations.locationCopy.previewIntroSuffix', {}, 'for å utelukke en lokasjon fra kjøringen.')}
                                </p>
                                <label className="flex items-center space-x-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={showChangesOnly}
                                        onChange={(e) => setShowChangesOnly(e.target.checked)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span>{t('manager.modules.adminItemsLocations.locationCopy.showChangesOnly', {}, 'Vis kun felter med endringer')}</span>
                                </label>
                            </div>

                            <div className="border border-gray-200 rounded-lg overflow-auto max-h-[55vh]">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                        <tr>
                                            <th className="text-left p-2 font-medium text-gray-700 sticky left-0 bg-gray-50">{t('warehouse.general.bin', {}, 'Lokasjon')}</th>
                                            <th className="text-left p-2 font-medium text-gray-700">{t('manager.modules.adminItemsLocations.locationCopy.colField', {}, 'Felt')}</th>
                                            <th className="text-left p-2 font-medium text-gray-700">{t('manager.modules.adminItemsLocations.locationCopy.colBefore', {}, 'Før')}</th>
                                            <th className="text-center p-2"></th>
                                            <th className="text-left p-2 font-medium text-gray-700">{t('manager.modules.adminItemsLocations.locationCopy.colAfter', {}, 'Etter')}</th>
                                            <th className="text-right p-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeTargets.length === 0 && (
                                            <tr><td colSpan={6} className="p-6 text-center text-gray-400">{t('manager.modules.adminItemsLocations.locationCopy.allExcluded', {}, 'Alle lokasjoner er utelukket. Legg til minst én lokasjon for å fortsette.')}</td></tr>
                                        )}
                                        {activeTargets.flatMap(target => {
                                            const targetFields = FIELD_DEFS.filter(f => selectedFields.has(f.key));
                                            const rows = targetFields.map(field => {
                                                const before = field.display(target, master);
                                                const after = field.display(baseBin, master);
                                                const changed = before !== after;
                                                if (showChangesOnly && !changed) return null;
                                                return { field, before, after, changed };
                                            }).filter((r): r is { field: FieldDef; before: string; after: string; changed: boolean } => r !== null);

                                            if (rows.length === 0) {
                                                return [(
                                                    <tr key={target.binCode} className="border-b border-gray-100 bg-gray-50">
                                                        <td className="p-2 font-mono text-gray-900 sticky left-0 bg-gray-50">{target.binCode}</td>
                                                        <td colSpan={4} className="p-2 text-gray-500 italic">{t('manager.modules.adminItemsLocations.locationCopy.noChangesForFields', {}, 'Ingen endringer for valgte felter')}</td>
                                                        <td className="p-2 text-right">
                                                            <button
                                                                onClick={() => setExcludedBinCodes(prev => new Set(prev).add(target.binCode))}
                                                                className="text-red-600 hover:text-red-800"
                                                                title={t('manager.modules.adminItemsLocations.locationCopy.excludeLocation', {}, 'Utelukk lokasjonen')}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )];
                                            }
                                            return rows.map((r, idx) => (
                                                <tr key={`${target.binCode}-${r.field.key}`} className={`border-b border-gray-100 ${r.changed ? '' : 'opacity-60'}`}>
                                                    {idx === 0 ? (
                                                        <td className="p-2 font-mono text-gray-900 sticky left-0 bg-white border-r border-gray-100 align-top" rowSpan={rows.length}>
                                                            <div>{target.binCode}</div>
                                                            <div className="text-xs text-gray-500 font-sans">{target.description1 || '—'}</div>
                                                        </td>
                                                    ) : null}
                                                    <td className="p-2 text-gray-700">{r.field.label}</td>
                                                    <td className={`p-2 font-mono ${r.before === EMPTY ? 'text-gray-400 italic' : 'text-gray-700'}`}>{r.before}</td>
                                                    <td className="p-2 text-center"><ArrowRight className={`w-4 h-4 mx-auto ${r.changed ? 'text-green-600' : 'text-gray-300'}`} /></td>
                                                    <td className={`p-2 font-mono ${r.after === EMPTY ? 'text-gray-400 italic' : r.changed ? 'text-green-700 font-semibold' : 'text-gray-700'}`}>{r.after}</td>
                                                    {idx === 0 ? (
                                                        <td className="p-2 text-right align-top" rowSpan={rows.length}>
                                                            <button
                                                                onClick={() => setExcludedBinCodes(prev => new Set(prev).add(target.binCode))}
                                                                className="text-red-600 hover:text-red-800"
                                                                title={t('manager.modules.adminItemsLocations.locationCopy.excludeLocationFromRun', {}, 'Utelukk lokasjonen fra kjøringen')}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    ) : null}
                                                </tr>
                                            ));
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {excludedBinCodes.size > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start justify-between">
                                    <div className="flex items-start space-x-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                        <p className="text-sm text-amber-800">
                                            {t('manager.modules.adminItemsLocations.locationCopy.excludedSummary', { '0': excludedBinCodes.size, '1': Array.from(excludedBinCodes).join(', ') }, `${excludedBinCodes.size} lokasjon(er) utelukket fra kjøring: ${Array.from(excludedBinCodes).join(', ')}`)}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setExcludedBinCodes(new Set())}
                                        className="text-xs text-amber-700 hover:underline"
                                    >
                                        {t('manager.modules.adminItemsLocations.locationCopy.reset', {}, 'Tilbakestill')}
                                    </button>
                                </div>
                            )}

                            {receiptConflictBinCodes.length > 0 && (
                                <ReceiptBinWarningPanel binCodes={receiptConflictBinCodes} />
                            )}

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-sm text-blue-900">
                                    {t('manager.modules.adminItemsLocations.locationCopy.summaryCopying', {}, 'Kopierer')} <span className="font-semibold">{selectedFields.size}</span> {t('manager.modules.adminItemsLocations.locationCopy.summaryFieldsFromBase', {}, 'felt fra basislokasjon')}{' '}
                                    <span className="font-mono font-semibold">{baseBin.binCode}</span> {t('manager.modules.adminItemsLocations.locationCopy.summaryTo', {}, 'til')}{' '}
                                    <span className="font-semibold">{activeTargets.length}</span> {t('manager.modules.adminItemsLocations.locationCopy.summaryLocations', {}, 'lokasjon(er).')}
                                </p>
                            </div>
                        </div>
                    )}

                    {step === 'running' && (
                        <div className="flex flex-col items-center justify-center py-16 space-y-4">
                            <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
                            <p className="text-gray-700">{t('manager.modules.adminItemsLocations.locationCopy.runningMessage', { '0': activeTargets.length }, `Kopierer egenskaper til ${activeTargets.length} lokasjon(er)...`)}</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50">
                    <div className="text-xs text-gray-500">
                        {isLoadingMaster && (
                            <span className="flex items-center space-x-1">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span>{t('manager.modules.adminItemsLocations.locationCopy.loadingLookups', {}, 'Laster oppslagsdata...')}</span>
                            </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
                            disabled={isRunning}
                        >
                            {t('common.buttons.cancel', {}, 'Avbryt')}
                        </button>
                        {step === 'select-fields' && (
                            <button
                                onClick={() => setStep('select-base')}
                                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
                            >
                                {t('common.buttons.back', {}, 'Tilbake')}
                            </button>
                        )}
                        {step === 'preview' && (
                            <button
                                onClick={() => setStep('select-fields')}
                                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
                            >
                                {t('common.buttons.back', {}, 'Tilbake')}
                            </button>
                        )}
                        {step === 'select-base' && (
                            <button
                                onClick={() => setStep('select-fields')}
                                disabled={!baseBin}
                                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {t('common.buttons.next', {}, 'Neste')}
                            </button>
                        )}
                        {step === 'select-fields' && (
                            <button
                                onClick={() => setStep('preview')}
                                disabled={selectedFields.size === 0}
                                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {t('manager.modules.adminItemsLocations.locationCopy.nextWithCount', { '0': selectedFields.size }, `Neste (${selectedFields.size} felt)`)}
                            </button>
                        )}
                        {step === 'preview' && (
                            <button
                                onClick={executeRun}
                                disabled={activeTargets.length === 0 || selectedFields.size === 0 || isRunning}
                                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                            >
                                <Copy className="w-4 h-4" />
                                <span>{t('manager.modules.adminItemsLocations.locationCopy.runCopy', { '0': activeTargets.length }, `Kjør kopiering (${activeTargets.length} lokasjoner)`)}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const StepBadge: React.FC<{ n: number; label: string; active: boolean; done: boolean }> = ({ n, label, active, done }) => (
    <li className="flex items-center space-x-2">
        <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
            active ? 'bg-blue-600 text-white' :
            done ? 'bg-green-500 text-white' :
            'bg-gray-200 text-gray-600'
        }`}>
            {done ? <Check className="w-3 h-3" /> : n}
        </span>
        <span className={`text-sm ${active ? 'text-blue-700 font-medium' : done ? 'text-green-700' : 'text-gray-500'}`}>
            {label}
        </span>
    </li>
);

const Chevron: React.FC = () => (
    <span className="mx-3 text-gray-300">›</span>
);
